import { useState } from 'react'
import { useFormatters } from '../../contexts/PreferencesContext'
import {
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { TabProps } from './types'
import { ChartWrapper } from './ChartWrapper'
import { PPVChartPanels, TopNSelector } from './PPVCharts'
import { CostHistoricalView } from './CostHistoricalView'
import { useDrilldownAction } from '../drilldown'
import { RankedBarList } from './charts/RankedBarList'
import { OpenArrearsByDueMonthChart } from './charts/OpenArrearsByDueMonthChart'
import { IncomingByMonthCharts } from './charts/IncomingByMonthCharts'
import { ChartEmpty } from './charts/ChartEmpty'

export function CostTab({ data, filters, dataPeriod: _dataPeriod, orderType: _orderType }: TabProps) {
  const { formatCurrency, formatCompactCurrency } = useFormatters()
  const [activeCostTab, setActiveCostTab] = useState('future')
  // CR-3848: Add state for Highest Value Suppliers top N selection
  const [topHighestValueSuppliers, setTopHighestValueSuppliers] = useState(5)

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Scope-only filters (who/where, no date window) — shared by the drilldowns
  // whose rows are not bounded by the dashboard's date range.
  const scopeDrilldownFilters = {
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    // CR-7229: carry the warehouse scope into the drill-downs so the detail
    // list matches the tile it was opened from.
    ...(filters?.warehouse && { warehouse: filters.warehouse }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
  }

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...scopeDrilldownFilters,
  }

  // Arrears drilldown: exclude date_from/date_to since the dashboard computes
  // arrears on PO-level dates but drilldown applies them to delivery_date,
  // causing a mismatch. The overdue dynamic filter (delivery_date < today) is sufficient.
  const arrearsDrilldownFilters = scopeDrilldownFilters

  // CR-5456: PPV drilldown drops the dashboard window for the same reason.
  // The PPV bars are bucketed by promise / required date, which routinely sit
  // outside a window whose date_to is capped at the end of last month
  // (historical mode) — passing it through filtered every contracted line out
  // and the modal opened on "No records found" under a populated chart.
  // PPVChartPanels adds the clicked month (and view) instead.
  const ppvDrilldownFilters = scopeDrilldownFilters

  // CR-5454: Total Incoming drops the dashboard window for the same reason as
  // Arrears and PPV above. The card counts every open PO line regardless of
  // when it lands — its own "Incoming by Month" chart plots months past the
  // window's end — but the drilldown applies date_from/date_to to
  // delivery_date. Passing the window through made the card read 28 PO Lines
  // while the modal it opened showed 16, which is the count mismatch this
  // ticket is about.
  const incomingDrilldownFilters = scopeDrilldownFilters

  // Helper function to parse month string to date range
  const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
    try {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const parts = monthStr.trim().split(' ')
      const monthIndex = monthNames.indexOf(parts[0])
      if (monthIndex === -1) return null
      let year: number
      if (parts.length >= 2 && parts[1]) {
        year = parseInt(parts[1])
        if (year < 100) year += 2000
      } else {
        year = new Date().getFullYear()
      }
      // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
      const startDate = new Date(year, monthIndex, 1)
      const endDate = new Date(year, monthIndex + 1, 0)
      const fmt = (d: Date): string =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return {
        date_from: fmt(startDate),
        date_to: fmt(endDate),
      }
    } catch {
      return null
    }
  }

  // Handle chart click - open drill-down modal with month-specific data
  // CR-3918: Added 'ppv' metric type for PPV to Contract/Standard chart
  const handleChartClick = (chartData: unknown, metricType: 'spend' | 'arrears' | 'ppv' | 'open_order_value') => {
    if (!chartData) return
    const data = chartData as { activePayload?: Array<{ payload?: { month?: string } }>; activeLabel?: string }
    let month: string | null = null
    if (data.activePayload && data.activePayload[0]) {
      month = data.activePayload[0].payload?.month || null
    } else if (data.activeLabel) {
      month = data.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    openDrilldown(metricType, chartFilters, month)
  }

  // Get cost data from backend or use defaults
  const costData = data.costData || {
    total_incoming_value: 0,
    total_incoming_lines: 0,
    total_arrears_value: 0,
    total_arrears_lines: 0,
    total_suppliers: 0,
    incoming_by_week: [],
    arrears_by_supplier: [],
  }

  // Incoming by Month — 12 forward months of open-PO commitment by Latest
  // Supplier Promise Date (with fallback to delivery_date). Backend computes
  // this in costData.incoming_by_month so the Future view shows forward-
  // looking demand, not historical received GRN spend.
  const incomingByMonthValue = (costData.incoming_by_month || []) as Array<{ month: string; value: number; lines: number }>

  // CR-3847 + CR-3925: Incoming Per Week by Latest Supplier Promise Date
  // Now includes all 4 metrics: arrears value, arrears lines, incoming value, incoming lines
  // CR-5455 rework: carry week_start/week_end through so the click handler
  // can scope the drill-down to the clicked bucket.
  const incomingByWeekPromiseDate = (costData.incoming_by_week || []).map((w: { week: string; value: number; lines?: number; arrears_value?: number; arrears_lines?: number; week_start?: string; week_end?: string }) => ({
    week: w.week,
    incoming_value: w.value,
    incoming_lines: w.lines || 0,
    arrears_value: w.arrears_value || 0,
    arrears_lines: w.arrears_lines || 0,
    week_start: w.week_start,
    week_end: w.week_end,
  }))

  // CR-3966 / CR-5455 rework: dedicated click handler for the weekly chart.
  // QA flagged that this used to always open the generic 'arrears' metric
  // (wrong date field — delivery_date instead of the chart's own Latest
  // Supplier Promise Date — and no week scoping, so it returned an
  // unrelated ~6-month sample). Now opens 'incoming_per_week', scoped to
  // the clicked bar's own week_start/week_end, which filters on the same
  // effective date the chart buckets by.
  const handleWeekChartClick = (chartData: unknown) => {
    const data = chartData as { activePayload?: Array<{ payload?: { week?: string; week_start?: string; week_end?: string } }> }
    const payload = data?.activePayload?.[0]?.payload
    if (!payload) return
    // Both bounds or nothing. Spreading them conditionally meant a payload
    // missing either one opened an unscoped 'incoming_per_week' — every open
    // line, which is precisely the QA failure this ticket fixed. A click that
    // cannot be scoped to its own week does nothing instead.
    if (!payload.week_start || !payload.week_end) return
    openDrilldown(
      'incoming_per_week',
      {
        ...scopeDrilldownFilters,
        date_from: payload.week_start,
        date_to: payload.week_end,
      },
      payload.week ? `Incoming Per Week — ${payload.week}` : 'Incoming Per Week',
    )
  }

  // Arrears by suppliers data (value and count)
  // CR-3966: Keep full supplier name for drill-down filtering, truncate only for display via tick formatter
  const arrearsBySupplier = (costData.arrears_by_supplier || []).map((s: { supplier: string; arrears: number; count: number }) => ({
    supplier: s.supplier,
    arrears: s.arrears,
    count: s.count || 0,
  }))

  // Ranked-list rows for the arrears panel: by value, each row also carrying
  // the line count so one list answers both "who owes most" and "how many".
  const maxArrearsValue = Math.max(0, ...arrearsBySupplier.map(s => s.arrears || 0))
  const maxArrearsCount = Math.max(0, ...arrearsBySupplier.map(s => s.count || 0))
  const arrearsRanked = [...arrearsBySupplier]
    .sort((a, b) => (b.arrears || 0) - (a.arrears || 0))
    .map(s => ({
      key: s.supplier,
      label: s.supplier,
      bars: [
        { color: '#ef4444', fraction: maxArrearsValue ? (s.arrears || 0) / maxArrearsValue : 0, label: formatCurrency(s.arrears || 0) },
        { color: '#3b82f6', fraction: maxArrearsCount ? (s.count || 0) / maxArrearsCount : 0, label: `${s.count || 0} lines` },
      ],
    }))

  // CR-5456: PPV chart data, aggregated server-side from persisted per-line
  // PPV (CR-5957). Undefined for supplier users — the backend gates it out,
  // so the PPV panels simply don't render on the supplier portal.
  const ppvData = data.costData?.ppv

  // CR-3848 / CR-5457: Highest Value Suppliers — prefer the server-side
  // top_suppliers_by_open_value aggregation. The legacy fallback walks
  // incoming_lines (a 100-row sample of full-line values), which drifted
  // from the open-order book the KPI tile shows because (a) the sample
  // truncated, (b) total_value was full line × qty even for partially
  // received lines so the chart effectively read closed/received PO
  // value (Sarah's "GRN data" symptom).
  const serverTop = (costData as {
    top_suppliers_by_open_value?: Array<{ name: string; value: number }>
  }).top_suppliers_by_open_value
  const highestValueSuppliers = (
    serverTop && serverTop.length > 0
      ? serverTop
      : (costData.incoming_lines || [] as Array<{ supplier: string; total_value: number }>)
        .reduce(
          (acc: Array<{ name: string; value: number }>, line: { supplier: string; total_value: number }) => {
            const existing = acc.find(s => s.name === line.supplier)
            if (existing) {
              existing.value += line.total_value
            } else {
              acc.push({ name: line.supplier, value: line.total_value })
            }
            return acc
          },
          [] as Array<{ name: string; value: number }>,
        )
  )
    .slice()
    .sort((a: { value: number }, b: { value: number }) => b.value - a.value)
    .slice(0, topHighestValueSuppliers)

  const maxHighestValue = Math.max(0, ...highestValueSuppliers.map((s: { value: number }) => s.value || 0))
  const highestValueRanked = highestValueSuppliers.map((s: { name: string; value: number }) => ({
    key: s.name,
    label: s.name,
    bars: [{ color: '#8b5cf6', fraction: maxHighestValue ? (s.value || 0) / maxHighestValue : 0, label: formatCurrency(s.value || 0) }],
  }))

  return (
    <div className="space-y-4">
      {/* Sub-tabs for Future and Historical */}
      <div className="bg-card rounded-lg shadow-sm">
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveCostTab('future')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeCostTab === 'future'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Future
            </button>
            <button
              onClick={() => setActiveCostTab('historical')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeCostTab === 'historical'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Historical
            </button>
          </nav>
        </div>
        <div className="p-6">
          {activeCostTab === 'future' && (
            <div className="space-y-6">
              {/* CR-5454: Cost Overview KPI cards.
                  - Visual: dropped the blue-on-blue inner panels (was
                    bg-blue-700 inside bg-blue-600 — Sarah called the
                    contrast "intrusive"). Outer card stays brand-blue;
                    inner stat panels switched to white/transparent with
                    a subtle border for legibility.
                  - Click-through: each card now opens the matching
                    drilldown ('open_order_value' for Incoming, 'arrears' for
                    Arrears, 'suppliers' for Total Suppliers) so the
                    headline figures can be validated, per the spec.
                  - Rework (QA 2026-07-20): "Total Incoming" was wired to the
                    'spend' drilldown (GoodsReceiptNote — already-delivered
                    goods), so the modal population didn't match the card's
                    "N PO Lines" count. total_incoming_value/lines are
                    computed from PurchaseOrderLine filtered to
                    status__in=['pending','partially_received']
                    (see backend/purchasing/views/po_dashboard_calculators/cost.py),
                    which is exactly what the 'open_order_value' drilldown
                    metric queries — switched to that. */}
              <div className="grid grid-cols-3 gap-6">
                <button
                  type="button"
                  onClick={() => openDrilldown('open_order_value', incomingDrilldownFilters)}
                  className="bg-blue-600 text-white p-6 rounded-lg text-center hover:bg-blue-700 transition-colors text-left"
                  title="Drill into incoming PO lines"
                >
                  <h3 className="text-lg font-medium mb-2 text-center">Total Incoming</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">Value</div>
                      <div className="text-xl font-bold">{formatCurrency(costData.total_incoming_value || data.cost?.totalSpend || 0)}</div>
                    </div>
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">No.of PO Lines</div>
                      <div className="text-xl font-bold">{(costData.total_incoming_lines || data.purchasing?.totalLines || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => openDrilldown('arrears', arrearsDrilldownFilters)}
                  className="bg-blue-600 text-white p-6 rounded-lg text-center hover:bg-blue-700 transition-colors text-left"
                  title="Drill into arrears PO lines"
                >
                  <h3 className="text-lg font-medium mb-2 text-center">Total Arrears</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">Value</div>
                      <div className="text-xl font-bold">{formatCurrency(costData.total_arrears_value || data.cost?.historicalArrears || 0)}</div>
                    </div>
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">No.of PO Lines</div>
                      <div className="text-xl font-bold">{(costData.total_arrears_lines || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => openDrilldown('suppliers_with_open_pos', drilldownFilters)}
                  className="bg-blue-600 text-white p-6 rounded-lg text-center hover:bg-blue-700 transition-colors"
                  title="Drill into suppliers with at least one open PO line"
                >
                  <h3 className="text-lg font-medium mb-2">Suppliers with Open POs</h3>
                  <div className="text-4xl font-bold mt-4">{costData.total_suppliers || 0}</div>
                </button>
              </div>

              {/* Charts Row 1 - Incoming by Month (Value and Lines) */}
              <IncomingByMonthCharts
                months={incomingByMonthValue}
                onChartClick={(chartData) => handleChartClick(chartData, 'open_order_value')}
              />

              {/* CR-3847 + CR-3925: Incoming Per Week by Latest Supplier Promise Date - Single combined chart */}
              <ChartWrapper
                title="Incoming Per Week by Latest Supplier Promise Date"
                data={incomingByWeekPromiseDate}
                exportFilename="incoming-by-week"
                legend={[
                  { color: '#3b82f6', label: 'Total Incoming Value' },
                  { color: '#ef4444', label: 'Arrears to Date Value' },
                  { color: '#10b981', label: 'Total Incoming PO Lines' },
                  { color: '#f59e0b', label: 'Arrears to Date PO Lines' },
                ]}
              >
                <div
                  // Recharts 3 fires a chart-level onClick only when the pointer
                  // is over a rendered element, and populates activePayload only
                  // in that same case. A click in the gap between bars — or above
                  // them — therefore does nothing, even though the tooltip names
                  // the week. Resolve such clicks against the x-axis tick
                  // geometry so anywhere in the plot area drills into the week
                  // the user is pointing at.
                  onClick={(e) => {
                    if (document.querySelector('[role="dialog"]')) return;
                    const host = e.currentTarget as HTMLElement;
                    const ticks = Array.from(
                      host.querySelectorAll<SVGGElement>('.recharts-xAxis .recharts-cartesian-axis-tick'),
                    );
                    if (!ticks.length || !incomingByWeekPromiseDate.length) return;
                    let best = -1;
                    let bestDist = Infinity;
                    ticks.forEach((tick, i) => {
                      const r = tick.getBoundingClientRect();
                      const d = Math.abs(r.x + r.width / 2 - e.clientX);
                      if (d < bestDist) { bestDist = d; best = i; }
                    });
                    const point = incomingByWeekPromiseDate[best];
                    if (point) handleWeekChartClick({ activePayload: [{ payload: point }] });
                  }}
                  style={{ cursor: 'pointer' }}
                >
                {incomingByWeekPromiseDate.every((w) => !w.incoming_value && !w.arrears_value && !w.incoming_lines && !w.arrears_lines) ? (
                  <ChartEmpty height={280} testId="cost-incoming-week-empty"
                              message="Nothing open is due by promise date in these weeks." />
                ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart
                    data={incomingByWeekPromiseDate}
                    onClick={handleWeekChartClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompactCurrency(value)} />
                    <YAxis yAxisId="lines" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip
                      cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                      formatter={(value: number, name: string) =>
                        name.includes('Value') ? formatCurrency(value) : `${value} lines`
                      }
                    />
                    <Bar yAxisId="value" dataKey="incoming_value" fill="#3b82f6" name="Total Incoming Value" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="value" dataKey="arrears_value" fill="#ef4444" name="Arrears to Date Value" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="lines" dataKey="incoming_lines" fill="#10b981" name="Total Incoming PO Lines" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="lines" dataKey="arrears_lines" fill="#f59e0b" name="Arrears to Date PO Lines" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
                )}
                </div>
              </ChartWrapper>

              {/* Charts Row 2 - Arrears By Supplier (value + line count, one ranked list) */}
              {/* CR-3966: drill-down passes the clicked supplier's name */}
              <ChartWrapper
                title="Arrears By Supplier"
                data={arrearsBySupplier}
                exportFilename="arrears-by-supplier"
                legend={[
                  { color: '#ef4444', label: 'Arrears value' },
                  { color: '#3b82f6', label: 'Arrears PO lines' },
                ]}
              >
                <RankedBarList
                  items={arrearsRanked}
                  onSelect={(supplierName) => openDrilldown('arrears', {
                    ...arrearsDrilldownFilters,
                    supplier_name: supplierName,
                  }, supplierName)}
                />
              </ChartWrapper>

              {/* W3-T14 (FIX-04): the live backlog by the month it was due, beside its weekly sibling */}
              <OpenArrearsByDueMonthChart data={data.charts?.openArrearsByDueMonth || []} onChartClick={(c) => handleChartClick(c, 'arrears')} />

              {/* Charts Row 3 - PPV Section (CR-5456) */}
              {/* Future PPV: committed variance on outstanding quantity, by
                  supplier promise date, with overdue lines as Arrears.
                  Hidden entirely for supplier users (no data from backend). */}
              {ppvData && (
                <PPVChartPanels view={ppvData.future} isFuture={true} drilldownFilters={ppvDrilldownFilters} />
              )}

              {/* Charts Row 5 - Highest Value Suppliers */}
              {/* CR-3848: Added Top 5/10/20 selector */}
              <ChartWrapper
                title="Highest Value Suppliers"
                data={highestValueSuppliers}
                exportFilename="highest-value-suppliers"
                headerActions={
                  <TopNSelector value={topHighestValueSuppliers} onChange={setTopHighestValueSuppliers} />
                }
              >
                {/* CR-5457 rework: drill into the OPEN ORDER BOOK
                    (open_order_value → PurchaseOrderLine, status
                    pending/partially_received), NOT 'spend' which lists
                    goods-received (GRN) rows. Pass the clicked supplier so the
                    drill-down scopes to it. */}
                <RankedBarList
                  items={highestValueRanked}
                  onSelect={(supplierName) => openDrilldown('open_order_value', {
                    ...drilldownFilters,
                    supplier_name: supplierName,
                  }, supplierName)}
                />
              </ChartWrapper>
            </div>
          )}

          {activeCostTab === 'historical' && (
            <CostHistoricalView
              data={data}
              dataMode={filters?.dataMode}
              ppvView={ppvData?.historical}
              ppvDrilldownFilters={ppvDrilldownFilters}
              onChartClick={handleChartClick}
            />
          )}
        </div>
      </div>
    </div>
  )
}
